import i18n from "i18next";
import { z } from "zod";
import { getAtcAfterAtsError, normalizeDate } from "../utils";

export const PORT_SERVICE_FIELDS_LANG_BASE = "vessel-call.port-services.fields";

const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;
type RequiredMessageKey = "required" | "mandatory";

export const parseDateLikeValue = (value: unknown) => {
  if (value === "" || value === null || value === undefined) return undefined;

  if (typeof value === "string" || typeof value === "number" || value instanceof Date) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed;
  }

  return value;
};

const parsePositiveIntegerLikeValue = (value: unknown) => {
  if (value === "" || value === null || value === undefined) return undefined;

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    return POSITIVE_INTEGER_PATTERN.test(trimmedValue) ? Number(trimmedValue) : value;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

export const createAtcSchema = (
  requiredMessageKey: RequiredMessageKey = "required",
): z.AnyZodObject =>
  z.object({
    atc: z.preprocess(
      parseDateLikeValue,
      z.date({
        required_error: i18n.t(`form-validation.${requiredMessageKey}`, { label: "ATC" }),
        invalid_type_error: i18n.t("form-validation.invalid-date", { label: "ATC" }),
      }),
    ),
  });

export const withAtcAfterAtsValidation = <TSchema extends z.ZodTypeAny>(
  schema: TSchema,
  ats?: string | Date | null,
) =>
  schema.superRefine((value, ctx) => {
    const error = getAtcAfterAtsError((value as { atc?: string | Date | null }).atc, ats);

    if (!error) {
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["atc"],
      message: error,
    });
  });

export const getAtcIncludesResourceEndTimesError = (
  atc: Date | string | null | undefined,
  resourceEndTimes: Array<Date | string | null | undefined>,
) => {
  const normalizedAtc = normalizeDate(atc);

  if (!normalizedAtc) {
    return undefined;
  }

  const hasResourceOutsideAtc = resourceEndTimes.some((endTime) => {
    const normalizedEndTime = normalizeDate(endTime);
    return normalizedEndTime != null && normalizedEndTime > normalizedAtc;
  });

  return hasResourceOutsideAtc
    ? i18n.t(`${PORT_SERVICE_FIELDS_LANG_BASE}.error-message-atc-resource-end-times`)
    : undefined;
};

const getAtcMinDateFromAts = (ats: Date | string | null | undefined) => {
  const normalizedAts = normalizeDate(ats);

  if (!normalizedAts) {
    return undefined;
  }

  const minDate = new Date(normalizedAts);
  minDate.setSeconds(0, 0);
  minDate.setMinutes(minDate.getMinutes() + 1);

  return minDate;
};

export const getAtcMinDate = (
  ats: Date | string | null | undefined,
  resourceEndTimes: Array<Date | string | null | undefined>,
) => {
  const atsMinDate = getAtcMinDateFromAts(ats);
  const latestResourceEndTime = resourceEndTimes.reduce<Date | undefined>((latest, endTime) => {
    const normalizedEndTime = normalizeDate(endTime);

    if (!normalizedEndTime) {
      return latest;
    }

    if (!latest || normalizedEndTime > latest) {
      return normalizedEndTime;
    }

    return latest;
  }, undefined);

  if (!atsMinDate) {
    return latestResourceEndTime ? new Date(latestResourceEndTime) : undefined;
  }

  if (!latestResourceEndTime || atsMinDate >= latestResourceEndTime) {
    return atsMinDate;
  }

  return new Date(latestResourceEndTime);
};

export const createActualQuantitySchema = () =>
  z.object({
    actualQuantity: z.preprocess(
      parsePositiveIntegerLikeValue,
      z
        .number({
          required_error: i18n.t("form-validation.required", {
            label: i18n.t(`${PORT_SERVICE_FIELDS_LANG_BASE}.actual-quantity`),
          }),
          invalid_type_error: i18n.t("form-validation.invalid-number", {
            label: i18n.t(`${PORT_SERVICE_FIELDS_LANG_BASE}.actual-quantity`),
          }),
        })
        .int(
          i18n.t("form-validation.invalid-number", {
            label: i18n.t(`${PORT_SERVICE_FIELDS_LANG_BASE}.actual-quantity`),
          }),
        )
        .positive(
          i18n.t("form-validation.mandatory-min", {
            label: i18n.t(`${PORT_SERVICE_FIELDS_LANG_BASE}.actual-quantity`),
            min: 1,
          }),
        ),
    ),
  });
